/**
 * schema.js — constants, enumerations, and alias helpers for the Missions domain.
 * Pure: no imports, no state, no React. Aliases follow the Missions path namespace (single root session).
 */

// A mission's own status, which lives in the graph object's metadata rather than on a vertex. The
// heartbeat reconciles a mission only while it is `active` — anything else is summarised with zero
// cells and skipped — so a mission written without one is invisible to the orchestrator.
export const MISSION_STATUS = { ACTIVE: 'active', ARCHIVED: 'archived' };

// (The bootstrap `cell/created` key lived here while this widget wrote that record itself. The
// orchestrator posts it on activation now — see client.addCell — and nothing in the browser reads a
// record's `key`, so the constant went with the write.)

// Cell status vocabulary, in lifecycle order (docs/external/cell_states.md). `inactive` = a person's pause.
//
// There is no `ready`. It named the moment a cell's prerequisites were satisfied, and the
// orchestrator never stored it: readiness and advance ran in the same tick, which writes the vertex
// once at its end, so the value was assigned and overwritten before anything could observe it.
// Readiness is a predicate re-derived every tick now, and `planned` runs straight into `working`.
export const STATUSES = [
  'draft', 'planned', 'working', 'input_needed', 'inactive', 'completed', 'failed'
];
export const TERMINAL_STATUSES = ['completed', 'failed'];
// The statuses the heartbeat SEES — and so the ones where the screen can change without anybody
// touching it. `draft` and `inactive` are waiting on a person to start or resume them; `completed`
// and `failed` are done. `input_needed` is here because a question arriving, and the run picking up
// once it is answered, are both things a person watches for. This is what a mission means by "there
// is work in flight", and it is why the Missions widget holds session sync open (widgetBase.js).
export const HEARTBEAT_STATUSES = ['planned', 'working', 'input_needed'];
export const NONTERMINAL_STATUSES = ['draft', 'planned', 'working', 'input_needed', 'inactive'];

export function statusPillClass(status) {
  if (status === 'failed' || status === 'input_needed') return 'title-tag-badge-warning';
  // `archived` is a MISSION status and reads muted for the same reason `inactive` does: nothing is
  // running. The teal default says "steady state", which an archived mission technically is and
  // which cost somebody an afternoon — the orchestrator never re-reads one, so a cell added to it
  // is silently never picked up, and the panel looked identical to a live mission.
  if (status === 'inactive' || status === MISSION_STATUS.ARCHIVED) return 'title-tag-badge-muted';
  return 'title-tag-badge';
}

/**
 * The label the orchestrator SYNTHESISES for a step that could not branch.
 *
 * A run exits on one of a small declared set — a step lists its `labels`, every outgoing edge its
 * `when`, and the run picks one: `approved`, `changes`, `done`, whatever the workflow's author
 * named. A step with a single unconditional edge has nothing to pick from, so the orchestrator
 * fills in `ok` (`SINGLE_EXIT_LABEL` in its own `workflow.py`) to keep the run log's shape uniform.
 *
 * It is therefore the one exit value that is not a decision, and surfaces that report what a run
 * CHOSE suppress it rather than badging every non-branching run with a word that says nothing. The
 * test is the VALUE, never "did this step have one edge": the orchestrator owns which value means
 * "no choice", and a UI re-deriving that from the graph would be a second, drifting copy of a rule
 * that already has an owner.
 */
export const SINGLE_EXIT_LABEL = 'ok';

export function isSingleExitLabel(label) {
  return label === SINGLE_EXIT_LABEL;
}

// Two step types; each yields { content, label }. workspace_init is gone (workspace is a cell property).
export const STEP_TYPES = ['worker_task', 'external_input'];
// The input_needed request kind (surfaced where input is shown — NOT on the step editor).
export const REQUEST_KINDS = ['approval', 'choice', 'question', 'review'];
// Carryover forms selectable in the editor (advanced { last:N } / { step, runs:all } handled separately).
export const CARRYOVER_MODES = ['none', 'last', 'all', 'steps'];

// Workspaces are ACTUAL (not templates): { name, type: local_existing, work_folder }.
export const WORKSPACE_TYPES = ['local_existing'];
export const WORKSPACE_MODES = ['inherit_from_mission', 'specify_workspace', 'inherit_from_previous_cell'];
// Cell-workflow selection mode — parallels workspace_mode. "specify_workflow" picks a workflow now;
// both inherit modes resolve at runtime (from the mission default / the previous cell).
export const CELL_WORKFLOW_MODES = ['inherit_from_mission', 'specify_workflow', 'inherit_from_previous_cell'];

// Well-known session-global config objects. `app/<app-name>/<object>` is the platform's namespace
// for an app's own configuration — `missions/<mid>/…` below is the app's DATA, and is unrelated.
export const CELL_WORKFLOWS_ALIAS = 'app/missions/cell_workflows';
export const WORKSPACES_ALIAS = 'app/missions/workspaces';
export const POLICIES_ALIAS = 'app/missions/policies';
export const HEARTBEAT_STATE_ALIAS = 'app/missions/heartbeat_state';

// ---- Alias helpers (path namespace) ----
export function missionAlias(id) {
  return `missions/${id}/graph`;
}
export function isMissionAlias(alias) {
  return typeof alias === 'string' && /^missions\/[^/]+\/graph$/.test(alias);
}
export function missionIdFromAlias(alias) {
  const m = typeof alias === 'string' && alias.match(/^missions\/([^/]+)\/graph$/);
  return m ? m[1] : null;
}
export function cellMetaThreadAlias(missionId, cellId) {
  return `missions/${missionId}/cells/${cellId}/meta`;
}
// One agent session = one worker_task run, keyed `<step>.<run>`. `<step>` excludes `.`, so the key
// splits unambiguously on the last dot.
export function sessionKey(step, run) {
  return `${step}.${run}`;
}
export function sessionThreadAlias(missionId, cellId, step, run) {
  return `missions/${missionId}/cells/${cellId}/sessions/${sessionKey(step, run)}/thread`;
}
export function sessionsPrefix(missionId, cellId) {
  return `missions/${missionId}/cells/${cellId}/sessions/`;
}
export function artifactsPrefix(missionId, cellId, step, run) {
  return `missions/${missionId}/cells/${cellId}/sessions/${sessionKey(step, run)}/artifacts/`;
}
export function cellPrefix(missionId, cellId) {
  return `missions/${missionId}/cells/${cellId}/`;
}

// ---- Id minting (conform to the spec patterns) ----
const B36 = 'abcdefghijklmnopqrstuvwxyz0123456789';
function randToken(len) {
  let s = '';
  for (let i = 0; i < len; i++) s += B36[Math.floor(Math.random() * B36.length)];
  return s;
}
// The one minter for a mission id and a cell id alike. Nothing about the item reaches it — not its title, not
// the gesture that created it — because an id sits in every alias beneath the item and so can never change,
// while a title can.
//
// Eight base36 chars is 36^8 ≈ 2.8e12 ids. A collision is refused, differently for each kind:
// - A mission is a plain upload_session_object, which OVERWRITES by alias, so client.createMission checks the
//   id against the loaded session-object listing and throws. That listing is CACHED, so it catches an id this
//   surface already knows about, not one created concurrently elsewhere.
// - A cell is an `add_vertex`, which carries attribute_not_exists, so the platform REFUSES a duplicate rather
//   than silently resetting rev — the write is the check.
export function mintId() {                  // ^[a-z0-9]{8}$
  return randToken(8);
}
